import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { isManager, isAdmin } from '../utils/role';
import Avatar from '../components/Avatar';

const STATUS_COLORS = { PLANNING: '#3b82f6', ACTIVE: '#10b981', ON_HOLD: '#f59e0b', COMPLETED: '#6b7280', CANCELLED: '#ef4444' };
const PRIORITY_COLORS = { LOW: '#94a3b8', MEDIUM: '#3b82f6', HIGH: '#f59e0b', CRITICAL: '#ef4444' };
const TASK_COLORS = { TODO: '#94a3b8', IN_PROGRESS: '#3b82f6', IN_REVIEW: '#8b5cf6', DONE: '#10b981', BLOCKED: '#ef4444' };

function DonutChart({ data, size = 160 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 160 160">
        <circle cx="80" cy="80" r="60" fill="none" stroke="#e5e7eb" strokeWidth="20" />
        <text x="80" y="84" textAnchor="middle" fontSize="14" fill="#9ca3af">No data</text>
      </svg>
    </div>
  );
  const r = 60, cx = 80, cy = 80;
  let cumulative = 0;
  const arcs = data.filter(d => d.value > 0).map(d => {
    const pct = d.value / total;
    const start = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const end = cumulative * 2 * Math.PI - Math.PI / 2;
    const large = pct > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
    return { ...d, path: `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`, pct };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width={size} height={size} viewBox="0 0 160 160">
        {arcs.map((a, i) => (
          <path key={i} d={a.path} fill="none" stroke={a.color} strokeWidth="20" strokeLinecap="round" />
        ))}
        <text x="80" y="76" textAnchor="middle" fontSize="28" fontWeight="700" fill="#0f172a">{total}</text>
        <text x="80" y="96" textAnchor="middle" fontSize="11" fill="#6b7280">Total</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.filter(d => d.value > 0).map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
            <span style={{ color: '#374151' }}>{d.label}</span>
            <span style={{ fontWeight: 600, marginLeft: 'auto', color: '#0f172a' }}>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChart({ data, maxHeight = 140 }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: maxHeight + 30, paddingTop: 10 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{d.value}</span>
          <div style={{
            width: '100%', maxWidth: 48, height: Math.max((d.value / max) * maxHeight, 4),
            background: d.color, borderRadius: '6px 6px 2px 2px', transition: 'height 0.3s'
          }} />
          <span style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', lineHeight: 1.2 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function ProgressBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#6b7280' }}>{value}/{total} ({pct}%)</span>
      </div>
      <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

const TASK_STATUS_LABEL = { TODO: 'To Do', IN_PROGRESS: 'In Progress', IN_REVIEW: 'In Review', DONE: 'Done', BLOCKED: 'Blocked' };
const emptyProjectForm = { projectName: '', projectCode: '', companyId: '', description: '', startDate: '', endDate: '', status: 'PLANNING', priority: 'MEDIUM', manager: '', department: '', budget: '' };

export default function Dashboard() {
  const [tab, setTab] = useState('dashboard');
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Project form state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProjectForm);
  const [error, setError] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [pr, co] = await Promise.all([api.get('/projects'), api.get('/companies').catch(() => ({ data: [] }))]);
      setProjects(pr.data);
      setCompanies(co.data);
      const allTasks = [];
      for (const p of pr.data) {
        try { const tr = await api.get(`/projects/${p.id}/tasks`); allTasks.push(...tr.data); } catch {}
      }
      setTasks(allTasks);
    } catch {}
    setLoading(false);
  };

  const getCompanyName = (id) => { const c = companies.find(c => c.id === id); return c ? c.name : '-'; };

  // Project CRUD
  const saveProject = async () => {
    setError('');
    if (!form.projectName || !form.projectCode || !form.startDate || !form.endDate) { setError('Name, Code, Start and End dates are required'); return; }
    if (!form.companyId) { setError('Company is required'); return; }
    try {
      const payload = { ...form, companyId: Number(form.companyId) };
      if (editing) { await api.put(`/projects/${editing}`, payload); }
      else { await api.post('/projects', payload); }
      setShowForm(false); setEditing(null); setForm(emptyProjectForm); loadAll();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
  };

  const editProject = (p) => {
    setForm({ projectName: p.projectName, projectCode: p.projectCode, companyId: p.companyId || '', description: p.description || '', startDate: p.startDate, endDate: p.endDate, status: p.status, priority: p.priority, manager: p.manager || '', department: p.department || '', budget: p.budget || '' });
    setEditing(p.id); setShowForm(true); setError('');
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await api.delete(`/projects/${id}`);
    loadAll();
  };

  // Dashboard data
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'ACTIVE').length,
    completed: projects.filter(p => p.status === 'COMPLETED').length,
    overdue: projects.filter(p => p.status === 'ACTIVE' && p.endDate && new Date(p.endDate) < new Date()).length,
  };
  const statusData = Object.entries(STATUS_COLORS).map(([key, color]) => ({ label: key.replace('_', ' '), value: projects.filter(p => p.status === key).length, color }));
  const priorityData = Object.entries(PRIORITY_COLORS).map(([key, color]) => ({ label: key, value: projects.filter(p => p.priority === key).length, color }));
  const taskStatusData = Object.entries(TASK_COLORS).map(([key, color]) => ({ label: key.replace(/_/g, ' '), value: tasks.filter(t => t.status === key).length, color }));
  const tasksDone = tasks.filter(t => t.status === 'DONE').length;
  const tasksInProgress = tasks.filter(t => t.status === 'IN_PROGRESS').length;
  const tasksBlocked = tasks.filter(t => t.status === 'BLOCKED').length;
  const recentProjects = [...projects].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);

  const statusBadge = (s) => ({ PLANNING: 'badge-info', ACTIVE: 'badge-success', ON_HOLD: 'badge-warning', COMPLETED: 'badge-gray', CANCELLED: 'badge-danger' }[s] || 'badge-gray');
  const priorityBadge = (p) => ({ CRITICAL: 'badge-danger', HIGH: 'badge-warning', MEDIUM: 'badge-info', LOW: 'badge-gray' }[p] || 'badge-gray');

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e5e7eb' }}>
        {[
          { key: 'dashboard', label: '📊 Dashboard' },
          { key: 'projects', label: '📁 Projects' },
          { key: 'mytasks', label: '📋 My Tasks' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '12px 24px', fontSize: 15, fontWeight: 600, borderRadius: '8px 8px 0 0', border: 'none',
            background: tab === t.key ? '#fff' : 'transparent', color: tab === t.key ? '#1a56db' : '#6b7280',
            borderBottom: tab === t.key ? '2px solid #1a56db' : '2px solid transparent', cursor: 'pointer'
          }}>{t.label}</button>
        ))}
      </div>

      {/* =================== DASHBOARD TAB =================== */}
      {tab === 'dashboard' && (
        <div>
          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Total Projects', value: stats.total, color: '#1a56db', bg: '#dbeafe', icon: '📁' },
              { label: 'Active', value: stats.active, color: '#059669', bg: '#d1fae5', icon: '🚀' },
              { label: 'Completed', value: stats.completed, color: '#6b7280', bg: '#f3f4f6', icon: '✅' },
              { label: 'Overdue', value: stats.overdue, color: '#dc2626', bg: '#fee2e2', icon: '⚠️' },
            ].map((s, i) => (
              <div key={i} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#0f172a' }}>Projects by Status</h3>
              <DonutChart data={statusData} />
            </div>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#0f172a' }}>Projects by Priority</h3>
              <BarChart data={priorityData} />
            </div>
          </div>

          {/* Charts Row 2 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#0f172a' }}>Task Overview</h3>
              <DonutChart data={taskStatusData} />
            </div>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#0f172a' }}>Task Progress</h3>
              <ProgressBar label="Completed" value={tasksDone} total={tasks.length} color="#10b981" />
              <ProgressBar label="In Progress" value={tasksInProgress} total={tasks.length} color="#3b82f6" />
              <ProgressBar label="Blocked" value={tasksBlocked} total={tasks.length} color="#ef4444" />
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Overall Completion</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#059669' }}>
                  {tasks.length > 0 ? Math.round((tasksDone / tasks.length) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Recent Projects */}
          {recentProjects.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#0f172a' }}>Recent Projects</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                {recentProjects.map(p => (
                  <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'} onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[p.status] || '#94a3b8', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{p.projectName}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }}>
                        {p.manager && <Avatar name={p.manager} size={16} fontSize={7} />}
                        {p.projectCode} · {p.manager || 'No manager'}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 4, background: STATUS_COLORS[p.status] + '20', color: STATUS_COLORS[p.status] }}>{p.status}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 4, background: PRIORITY_COLORS[p.priority] + '20', color: PRIORITY_COLORS[p.priority] }}>{p.priority}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* =================== PROJECTS TAB =================== */}
      {tab === 'projects' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>{projects.length} project{projects.length !== 1 ? 's' : ''}</div>
            {isManager() && <button className="btn-primary" onClick={() => { setShowForm(true); setEditing(null); setForm(emptyProjectForm); setError(''); }}>+ New Project</button>}
          </div>

          {/* Project Form Modal */}
          {showForm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
              <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#0f172a' }}>{editing ? 'Edit Project' : 'New Project'}</h3>
                {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{error}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Company *</label>
                    <select value={form.companyId} onChange={e => setForm({ ...form, companyId: e.target.value })}>
                      <option value="">Select Company</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Project Name *</label>
                    <input value={form.projectName} onChange={e => setForm({ ...form, projectName: e.target.value })} placeholder="My Project" />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Project Code *</label>
                    <input value={form.projectCode} onChange={e => setForm({ ...form, projectCode: e.target.value })} placeholder="PM-001" disabled={!!editing} style={{ opacity: editing ? 0.6 : 1 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Start Date *</label>
                    <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>End Date *</label>
                    <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Status</label>
                    <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      <option>PLANNING</option><option>ACTIVE</option><option>ON_HOLD</option><option>COMPLETED</option><option>CANCELLED</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Priority</label>
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                      <option>LOW</option><option>MEDIUM</option><option>HIGH</option><option>CRITICAL</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Manager</label>
                    <input value={form.manager} onChange={e => setForm({ ...form, manager: e.target.value })} placeholder="Manager name" />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Department</label>
                    <input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="Engineering" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Budget</label>
                    <input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="0.00" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Description</label>
                    <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Project description..." />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button className="btn-primary" onClick={saveProject} style={{ flex: 1 }}>{editing ? 'Update' : 'Create'}</button>
                  {editing && isManager() && (
                    <button className="btn-danger" onClick={() => { deleteProject(editing); setShowForm(false); setEditing(null); }} style={{ padding: '10px 20px' }}>Delete</button>
                  )}
                  <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }} style={{ flex: 1 }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Projects List */}
          {projects.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div>No projects yet. Create your first project!</div>
            </div>
          ) : (
            <div className="card">
              <table>
                <thead>
                  <tr><th>Code</th><th>Project Name</th><th>Company</th><th>Manager</th><th>Status</th><th>Priority</th><th>Start</th><th>End</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {projects.map(p => (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p.id}`)}>
                      <td style={{ fontWeight: 600, color: '#1a56db' }}>{p.projectCode}</td>
                      <td>{p.projectName}</td>
                      <td>{getCompanyName(p.companyId)}</td>
                      <td>{p.manager || '-'}</td>
                      <td><span className={`badge ${statusBadge(p.status)}`}>{p.status}</span></td>
                      <td><span className={`badge ${priorityBadge(p.priority)}`}>{p.priority}</span></td>
                      <td style={{ color: '#6b7280', fontSize: 13 }}>{p.startDate}</td>
                      <td style={{ color: '#6b7280', fontSize: 13 }}>{p.endDate}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {isManager() && <button className="btn-ghost" onClick={() => editProject(p)} style={{ marginRight: 4, padding: '4px 10px', fontSize: 12 }}>Edit</button>}
                        {isManager() && <button className="btn-ghost" onClick={() => deleteProject(p.id)} style={{ padding: '4px 10px', fontSize: 12, color: '#dc2626' }}>Delete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* =================== MY TASKS TAB =================== */}
      {tab === 'mytasks' && (() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const myTasks = tasks.filter(t =>
          (t.assigneeId && Number(t.assigneeId) === Number(user.id)) ||
          (t.assigneeName && t.assigneeName === user.fullName) ||
          (t.assignee && t.assignee === user.fullName)
        );
        const myTodo = myTasks.filter(t => t.status === 'TODO');
        const myInProgress = myTasks.filter(t => t.status === 'IN_PROGRESS');
        const myInReview = myTasks.filter(t => t.status === 'IN_REVIEW');
        const myDone = myTasks.filter(t => t.status === 'DONE');
        const myBlocked = myTasks.filter(t => t.status === 'BLOCKED');
        const myOverdue = myTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'DONE');
        const getProjectName = (pid) => { const p = projects.find(p => p.id === pid); return p ? p.projectName : ''; };
        const getProjectCode = (pid) => { const p = projects.find(p => p.id === pid); return p ? p.projectCode : ''; };

        return (
          <div>
            {/* My Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total', value: myTasks.length, color: '#1a56db', bg: '#dbeafe' },
                { label: 'To Do', value: myTodo.length, color: '#6b7280', bg: '#f3f4f6' },
                { label: 'In Progress', value: myInProgress.length, color: '#3b82f6', bg: '#dbeafe' },
                { label: 'Done', value: myDone.length, color: '#059669', bg: '#d1fae5' },
                { label: 'Overdue', value: myOverdue.length, color: '#dc2626', bg: '#fee2e2' },
              ].map((s, i) => (
                <div key={i} className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {myTasks.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div>No tasks assigned to you yet.</div>
              </div>
            ) : (
              <div>
                {/* Overdue Tasks */}
                {myOverdue.length > 0 && (
                  <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid #dc2626' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 12 }}>⚠️ Overdue ({myOverdue.length})</h3>
                    {myOverdue.map(t => (
                      <div key={t.id} onClick={() => navigate(`/tasks/${t.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fef2f2', borderRadius: 6, marginBottom: 6, cursor: 'pointer' }}>
                        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, minWidth: 45 }}>{t.taskCode}</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{t.title}</span>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{getProjectCode(t.projectId)}</span>
                        <span className={`badge ${({ CRITICAL: 'badge-danger', HIGH: 'badge-warning', MEDIUM: 'badge-info', LOW: 'badge-gray' })[t.priority]}`} style={{ fontSize: 10 }}>{t.priority}</span>
                        <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>📅 {t.dueDate}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Active Tasks (TODO + IN_PROGRESS + IN_REVIEW + BLOCKED) */}
                {[
                  { label: 'In Progress', tasks: myInProgress, color: '#3b82f6' },
                  { label: 'To Do', tasks: myTodo, color: '#6b7280' },
                  { label: 'In Review', tasks: myInReview, color: '#8b5cf6' },
                  { label: 'Blocked', tasks: myBlocked, color: '#dc2626' },
                ].filter(g => g.tasks.length > 0).map(group => (
                  <div key={group.label} className="card" style={{ marginBottom: 16, borderLeft: `4px solid ${group.color}` }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: group.color, marginBottom: 12 }}>{group.label} ({group.tasks.length})</h3>
                    {group.tasks.map(t => (
                      <div key={t.id} onClick={() => navigate(`/tasks/${t.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, marginBottom: 6, cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'} onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}>
                        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, minWidth: 45 }}>{t.taskCode}</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{t.title}</span>
                        <span style={{ fontSize: 11, color: '#6b7280', background: '#e5e7eb', borderRadius: 4, padding: '1px 6px' }}>{getProjectCode(t.projectId)}</span>
                        <span className={`badge ${({ CRITICAL: 'badge-danger', HIGH: 'badge-warning', MEDIUM: 'badge-info', LOW: 'badge-gray' })[t.priority]}`} style={{ fontSize: 10 }}>{t.priority}</span>
                        {t.dueDate && (
                          <span style={{ fontSize: 11, color: new Date(t.dueDate) < new Date() ? '#dc2626' : '#6b7280', fontWeight: new Date(t.dueDate) < new Date() ? 600 : 400 }}>📅 {t.dueDate}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}

                {/* Completed Tasks */}
                {myDone.length > 0 && (
                  <div className="card" style={{ borderLeft: '4px solid #059669' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#059669', marginBottom: 12 }}>✅ Completed ({myDone.length})</h3>
                    {myDone.slice(0, 10).map(t => (
                      <div key={t.id} onClick={() => navigate(`/tasks/${t.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', borderRadius: 6, marginBottom: 4, cursor: 'pointer', opacity: 0.7 }}>
                        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, minWidth: 45 }}>{t.taskCode}</span>
                        <span style={{ flex: 1, fontSize: 13, color: '#6b7280', textDecoration: 'line-through' }}>{t.title}</span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{getProjectCode(t.projectId)}</span>
                      </div>
                    ))}
                    {myDone.length > 10 && <div style={{ fontSize: 12, color: '#9ca3af', padding: '4px 12px' }}>+ {myDone.length - 10} more completed</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
